import { apiClient } from '../api/apiClient';
import type { ApiResponse } from '@/types/assessment';

// Analytics Types
export interface AttemptDetails {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  assessment: {
    _id: string;
    title: string;
    category: {
      _id: string;
      name: string;
    };
    difficulty: string;
  };
  status: 'in_progress' | 'completed' | 'abandoned' | 'expired';
  score?: {
    percentage: number;
    correctAnswers: number;
    totalQuestions: number;
    isPassed: boolean;
  };
  timing: {
    startedAt: string;
    completedAt?: string;
    totalTimeSpent: number;
  };
  attemptNumber: number;
  isRetake: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AttemptSummary {
  totalAttempts: number;
  completedAttempts: number;
  averageScore: number;
  passRate: number;
  retakeRate: number;
}

export interface AssessmentAttemptsResponse {
  attempts: AttemptDetails[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  summary: AttemptSummary;
}

export interface QuestionAnalytics {
  questionId: string;
  questionText: string;
  category: string;
  difficulty: string;
  selectedAnswer: string | null;
  correctAnswer: string;
  options: {
    optionA: string;
    optionB: string;
    optionC: string;
    optionD: string;
  };
  isCorrect: boolean;
  timeSpent: number;
  explanation?: string;
}

export interface AttemptQuestionAnalytics {
  attempt: AttemptDetails;
  questions: QuestionAnalytics[];
  summary: {
    totalQuestions: number;
    answeredQuestions: number;
    correctAnswers: number;
    averageTimePerQuestion: number;
    categoryBreakdown: {
      category: string;
      total: number;
      correct: number;
      percentage: number;
    }[];
  };
}

export interface AssessmentStatistics {
  _id: string;
  name: string;
  totalAttempts: number;
  completedAttempts: number;
  averageScore: number;
  passRate: number;
  retakeRate: number;
  averageTimeSpent: number;
}

export interface StatisticsOverview {
  totalAssessments: number;
  totalAttempts: number;
  totalUsers: number;
  overallPassRate: number;
  averageScore: number;
  mostPopularAssessment?: {
    _id: string;
    title: string;
    attempts: number;
  };
  bestPerformingCategory?: {
    _id: string;
    name: string;
    averageScore: number;
  };
}

export interface AssessmentStatisticsResponse {
  statistics: AssessmentStatistics[];
  overview: StatisticsOverview;
}

export interface AnalyticsDashboard {
  overview: {
    totalUsers: number;
    totalAssessments: number;
    totalAttempts: number;
    completionRate: number;
    averageScore: number;
    passRate: number;
  };
  trends: {
    dailyAttempts: { date: string; count: number }[];
    weeklyScores: { week: string; averageScore: number }[];
    categoryTrends: { category: string; trend: number }[];
  };
  topPerformers: {
    userId: string;
    name: string;
    email: string;
    averageScore: number;
    completedAssessments: number;
  }[];
  popularAssessments: {
    assessmentId: string;
    title: string;
    attempts: number;
    averageScore: number;
  }[];
  recentActivity: {
    type: string;
    user: string;
    assessment: string;
    timestamp: string;
    score?: number;
  }[];
}

// Query Parameters
export interface AssessmentAttemptsQuery {
  search?: string;
  assessmentId?: string;
  categoryId?: string;
  userId?: string;
  status?: 'in_progress' | 'completed' | 'abandoned' | 'expired';
  difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
  startDate?: string;
  endDate?: string;
  isRetake?: boolean;
  minScore?: number;
  maxScore?: number;
  isPassed?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface StatisticsQuery {
  assessmentId?: string;
  categoryId?: string;
  startDate?: string;
  endDate?: string;
  groupBy?: 'assessment' | 'category' | 'difficulty' | 'month' | 'week';
}

export interface DashboardQuery {
  period?: 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all';
}

class AnalyticsService {
  private readonly baseUrl = '/analytics';

  /**
   * Get detailed assessment attempts with filters (Admin only)
   * GET /api/analytics/assessment-attempts
   */
  async getAssessmentAttempts(params?: AssessmentAttemptsQuery): Promise<ApiResponse<AssessmentAttemptsResponse>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `${this.baseUrl}/assessment-attempts?${queryString}` : `${this.baseUrl}/assessment-attempts`;
    
    return apiClient.get(url);
  }

  /**
   * Get detailed question-wise analytics for a specific attempt (Admin only)
   * GET /api/analytics/assessment-attempts/{attemptId}/questions
   */
  async getAttemptQuestionAnalytics(attemptId: string): Promise<ApiResponse<AttemptQuestionAnalytics>> {
    return apiClient.get(`${this.baseUrl}/assessment-attempts/${attemptId}/questions`);
  }

  /**
   * Get comprehensive assessment statistics (Admin only)
   * GET /api/analytics/assessments/statistics
   */
  async getAssessmentStatistics(params?: StatisticsQuery): Promise<ApiResponse<AssessmentStatisticsResponse>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `${this.baseUrl}/assessments/statistics?${queryString}` : `${this.baseUrl}/assessments/statistics`;
    
    return apiClient.get(url);
  }

  /**
   * Get analytics dashboard summary (Admin only)
   * GET /api/analytics/talky
   */
  async getAnalyticsDashboard(params?: DashboardQuery): Promise<ApiResponse<AnalyticsDashboard>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `${this.baseUrl}/talky?${queryString}` : `${this.baseUrl}/talky`;
    
    return apiClient.get(url);
  }

  /**
   * Export assessment attempts data
   * GET /api/analytics/assessment-attempts/export
   */
  async exportAssessmentAttempts(params?: AssessmentAttemptsQuery, format: 'csv' | 'xlsx' = 'xlsx'): Promise<Blob> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    queryParams.append('format', format);
    
    const response = await fetch(`/api${this.baseUrl}/assessment-attempts/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    return response.blob();
  }
}

export const analyticsService = new AnalyticsService();